/**
 * Development Override Utilities
 *
 * Provides secure role and tool overrides for development testing.
 * Only works for authorized users on localhost in development mode.
 */

const ALLOWED_OVERRIDE_EMAILS = [
  "logan.rogers@moonbug.com",
  "logan.rogers@moonbug.tech",
];

/**
 * Check if we're in development environment
 * Also checks for explicit local development mode flag
 */
export const isDevelopmentEnvironment = (): boolean => {
  return (
    process.env.NODE_ENV === "development" &&
    (process.env.REACT_APP_ENV_MODE === "local" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  );
};

/**
 * Check if we're running on localhost
 */
export const isLocalhost = (): boolean => {
  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
};

/**
 * Check if user is authorized to use dev overrides
 */
export const isAuthorizedUser = (userEmail: string | null): boolean => {
  return !!userEmail && ALLOWED_OVERRIDE_EMAILS.includes(userEmail.toLowerCase());
};

/**
 * Multi-layer security check for dev overrides
 */
export const canUseDevOverrides = (userEmail: string | null): boolean => {
  return (
    isDevelopmentEnvironment() && isLocalhost() && isAuthorizedUser(userEmail)
  );
};

/**
 * Get role override from environment variables
 */
export const getDevRoleOverride = (): string | null => {
  return process.env.REACT_APP_DEV_ROLE_OVERRIDE || null;
};

/**
 * Get tools override from environment variables
 */
export const getDevToolsOverride = (): string[] => {
  const toolsEnv = process.env.REACT_APP_DEV_TOOLS_OVERRIDE;
  if (!toolsEnv) return [];

  try {
    const parsed = JSON.parse(toolsEnv);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(
      "Failed to parse REACT_APP_DEV_TOOLS_OVERRIDE:",
      toolsEnv,
      error
    );
    return [];
  }
};

/**
 * Check if any overrides are currently active
 */
export const hasActiveOverrides = (userEmail: string | null): boolean => {
  if (!canUseDevOverrides(userEmail)) return false;

  const roleOverride = getDevRoleOverride();
  const toolsOverride = getDevToolsOverride();

  return !!(roleOverride || toolsOverride.length > 0);
};

/**
 * Get override status for display/logging
 */
export const getOverrideStatus = (userEmail: string | null) => {
  if (!canUseDevOverrides(userEmail)) {
    return { canOverride: false, hasOverrides: false };
  }

  const roleOverride = getDevRoleOverride();
  const toolsOverride = getDevToolsOverride();
  const hasOverrides = !!(roleOverride || toolsOverride.length > 0);

  return {
    canOverride: true,
    hasOverrides,
    roleOverride,
    toolsOverride,
  };
};
